import { createSlice } from '@reduxjs/toolkit';

import { setLike } from 'store/likeCount/action';

import { fetchReviewList, fetchSelectedReviewList } from './actions';

import type { ReviewListState } from './types';

const initialState: ReviewListState = {
  data: { offset: 0, limit: 10, total: 0, contents: [] },
  isFetching: false,
  selectedReviewData: { offset: 0, limit: 1, total: 0, contents: [] },
  isSelectedReview: false,
};

const reviewListSlice = createSlice({
  name: 'reviewList',
  initialState,
  reducers: {
    updateIsSelectedReview: (state, action) => {
      state.isSelectedReview = action.payload;
    },
    updateReviewLikeOptimistic: (state, action) => {
      const { reviewId, isLiked } = action.payload;
      const review = state.data.contents.find((review) => review.id === reviewId);

      if (review) {
        review.isLiked = isLiked;
        review.likeCount = isLiked ? review.likeCount + 1 : Math.max(0, review.likeCount - 1);
      }
    },
    resetReviewList: (state) => {
      state.data = { offset: 0, limit: 10, total: 0, contents: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewList.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchReviewList.fulfilled, (state, action) => {
        const newData = action.payload;

        if (newData.offset === 0) {
          // Initial load
          state.data = newData;
        } else {
          // Appending new paginated data
          state.data.contents = [...state.data.contents, ...newData.contents];
          state.data.offset = newData.offset;
          state.data.limit = newData.limit;
          state.data.total = newData.total;
        }

        state.selectedReviewData = {
          offset: 0,
          limit: 1,
          total: 0,
          contents: [],
        };

        state.isFetching = false;
      })
      .addCase(fetchReviewList.rejected, (state) => {
        state.data = { offset: 20, limit: 20, total: 0, contents: [] };
      })
      .addCase(fetchSelectedReviewList.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchSelectedReviewList.fulfilled, (state, action) => {
        const newData = action.payload;

        state.isSelectedReview = true;
        state.selectedReviewData.contents = newData.contents;
        state.isFetching = false;
      })
      .addCase(fetchSelectedReviewList.rejected, (state) => {
        state.isFetching = false;
        state.selectedReviewData = { offset: 0, limit: 1, total: 0, contents: [] };
      })
      .addCase(setLike.rejected, (state, action) => {
        const review = state.data.contents.find((review) => review.id === action.meta.arg.reviewId);

        if (review) {
          review.isLiked = !action.meta.arg.isLiked;
          review.likeCount = action.meta.arg.isLiked
            ? Math.max(0, review.likeCount - 1)
            : review.likeCount + 1;
        }
      });
  },
});

export const { updateReviewLikeOptimistic, resetReviewList, updateIsSelectedReview } =
  reviewListSlice.actions;

export const reviewListReducer = reviewListSlice.reducer;
